import React from 'react';
import { motion } from "framer-motion";
import { ShieldCheck, CheckCircle2, Globe, FileKey, ShieldAlert, LockKeyhole } from "lucide-react";
import { Card, CardContent } from "@/shared/components/ui/card";

const Compliance = () => {
  return (
    <div className="pt-32 pb-20 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-slate-400">Enterprise Grade Compliance</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Global <span className="gradient-text">Compliance & Security</span>
          </h1>
          <p className="text-lg text-slate-400">
            We meet the most stringent industry standards globally. Partner with confidence knowing that your payments and customer data are heavily protected and audited.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {[
            {
              icon: ShieldAlert,
              title: "PCI-DSS Level 1",
              description: "The highest level of certification in the payment industry. Our entire vault and infrastructure are verified annually by independent QSA audits.",
              color: "text-blue-400",
              bgColor: "bg-blue-500/10",
              borderColor: "border-blue-500/20"
            },
            {
              icon: Globe,
              title: "GDPR Compliant",
              description: "We are fully compliant with the EU General Data Protection Regulation, providing robust data privacy rules and user consent mechanisms out-of-the-box.",
              color: "text-purple-400",
              bgColor: "bg-purple-500/10",
              borderColor: "border-purple-500/20"
            },
            {
              icon: FileKey,
              title: "SOC 2 Type II",
              description: "Our security controls, availability, routing processing integrity, and privacy principles are continuously audited for SOC 2 Type II compliance.",
              color: "text-emerald-400",
              bgColor: "bg-emerald-500/10",
              borderColor: "border-emerald-500/20"
            },
            {
              icon: ShieldCheck,
              title: "AML & KYC Embedded",
              description: "We supply our engines with real-time Anti-Money Laundering and Know-Your-Customer onboarding flow APIs designed to meet global regulator thresholds.",
              color: "text-amber-400",
              bgColor: "bg-amber-500/10",
              borderColor: "border-amber-500/20"
            },
            {
              icon: LockKeyhole,
              title: "PSD2 / SCA Secure",
              description: "Our checkout flows dynamically invoke 3D Secure 2.0 and meet the Strong Customer Authentication (SCA) requirements mapped by PSD2 across Europe.",
              color: "text-rose-400",
              bgColor: "bg-rose-500/10",
              borderColor: "border-rose-500/20"
            },
            {
              icon: CheckCircle2,
              title: "ISO 27001",
              description: "We govern all our data frameworks and asset lifecycles via the internationally recognized ISO/IEC 27001 standard for information security management.",
              color: "text-cyan-400",
              bgColor: "bg-cyan-500/10",
              borderColor: "border-cyan-500/20"
            }
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <Card className="bg-slate-900/50 border-slate-800/50 hover:border-slate-700 transition-colors h-full">
                <CardContent className="p-8">
                  <div className={`w-14 h-14 rounded-xl ${item.bgColor} border ${item.borderColor} flex items-center justify-center mb-6`}>
                    <item.icon className={`h-7 w-7 ${item.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-tr from-blue-900/20 to-violet-900/20 border border-slate-800/50 rounded-3xl p-8 md:p-12 mb-12 flex flex-col md:flex-row items-center justify-between gap-8 gap-y-12"
        >
          <div className="max-w-xl">
             <h2 className="text-3xl font-bold text-white mb-6">Request A Compliance Report</h2>
             <p className="text-slate-300 text-lg leading-relaxed">
               We make our SOC 2 Type II, ISO 27001, and PCI-DSS Level 1 Attestation of Compliance (AoC) reports available to enterprise prospects under a mutual Non-Disclosure Agreement.
             </p>
           </div>
           <div>
             <button className="bg-white text-slate-900 hover:bg-slate-100 font-semibold px-8 py-4 rounded-xl transition-colors whitespace-nowrap">
               Contact Compliance Team
             </button>
           </div>
        </motion.div>

      </div>
    </div>
  );
};

export default Compliance;
